/**
 * Historic Encounters 5–8. Same rules as `historic-scenes-a.ts`: the prose and
 * the two decoded bytes per seat are the file's, the geometry is this port's
 * reading of the prose, and every invention is named in `portNotes`.
 */

import { BALLOON_ALTITUDE_M, quarry, wingman, type HistoricEncounterData, type HistoricFlight } from "./historic-scene.ts";

/** Udet, arriving over the threatened balloon unit with two of his Staffel. */
const UDET_LEADER: HistoricFlight = {
  id: "udet",
  nation: "german",
  aircraft: "FOKD7",
  callsign: "Ernst Udet",
  skill: "ace",
  aceId: "udet",
  seat: "german",
  x: 0,
  altitudeM: 1500,
  z: 3800,
  headingRad: 0,
};

/** The S.E.5s already closing on the gasbags when he gets there. */
const SE5_LEADER: HistoricFlight = {
  id: "se5-1",
  nation: "allied",
  aircraft: "SE5",
  callsign: "S.E.5 leader",
  skill: "veteran",
  seat: "allied",
  x: 0,
  altitudeM: 1400,
  z: 1900,
  headingRad: Math.PI,
};

/** The pair of B.E.2c raiders Immelmann found on their way home. */
const BE2_LEADER = quarry("be2-1", "BE2", "B.E.2c (1)", "novice", [-190, 2450, 4150]);

/** The Jagdstaffel standing guard over the Meuse balloon line. */
const GUARD_LEADER: HistoricFlight = {
  id: "guard-1",
  nation: "german",
  aircraft: "FOKD7",
  callsign: "Balloon guard",
  skill: "veteran",
  seat: "german",
  x: 340,
  altitudeM: 2150,
  z: 3180,
  headingRad: 0,
};

/** Richthofen, with one more of Jasta 11 on him. */
const JASTA11_LEADER: HistoricFlight = {
  id: "richthofen",
  nation: "german",
  aircraft: "ALBA_D3",
  callsign: "Manfred von Richthofen",
  skill: "ace",
  aceId: "richthofen",
  seat: "german",
  x: 0,
  altitudeM: 2100,
  z: 2950,
  headingRad: 0,
};

/** The two F.E.2bs of 20 Squadron that are left. */
const FE2_LEADER = quarry("fe2-1", "FE2", "F.E.2b (1)", "regular", [-120, 1850, 2620]);

/** Naval 10's Triplanes, arriving. */
const TRIPLANE_LEADER: HistoricFlight = {
  id: "triplane-1",
  nation: "allied",
  aircraft: "SOPTRI",
  callsign: "Naval 10 leader",
  skill: "veteran",
  seat: "allied",
  x: -420,
  altitudeM: 2350,
  z: 1650,
  headingRad: Math.PI,
};

/**
 * **5. Udet Defends A Drachen** — the mirror of encounter 7. German gasbags,
 * a German guard arriving late, and S.E.5s already on top of them.
 */
const UDET: HistoricEncounterData = {
  id: "historic-5",
  title: "Udet Defends A Drachen",
  when: "Summer 1918",
  era: "late",
  region: "verdun",
  weather: { sky: "clear", wind: "light", timeOfDay: "daytime" },
  briefing: [
    "Ernst Udet received a frantic call for help from a nearby German",
    "balloon unit.  Several Allied planes were in the area, and the German",
    "observation balloons needed protection.  Taking to the air, Udet and his",
    "men arrived on the scene just as the British S.E.5s closed on the German",
    "balloons.",
  ],
  balloonNation: "german",
  balloons: [
    { id: "balloon-1", nation: "german", label: "Drachen 1", x: -420, altitudeM: BALLOON_ALTITUDE_M, z: 2600 },
    { id: "balloon-2", nation: "german", label: "Drachen 2", x: 380, altitudeM: BALLOON_ALTITUDE_M, z: 2800 },
  ],
  flights: [
    UDET_LEADER,
    wingman(UDET_LEADER, { id: "jasta-2", callsign: "Jasta 4 (2)", skill: "veteran", dx: -240, dy: -40, dz: 120 }),
    wingman(UDET_LEADER, { id: "jasta-3", callsign: "Jasta 4 (3)", skill: "regular", dx: 250, dy: -30, dz: 120 }),
    SE5_LEADER,
    wingman(SE5_LEADER, { id: "se5-2", callsign: "S.E.5 (2)", skill: "regular", dx: -300, dy: -30, dz: -120 }),
    wingman(SE5_LEADER, { id: "se5-3", callsign: "S.E.5 (3)", skill: "regular", dx: 310, dy: 30, dz: -120 }),
  ],
  german: {
    aceIndex: 1,
    aircraftCode: 24,
    playable: true,
    lines: [
      "You are Udet.  You start as your squadron arrives near the threatened",
      "balloon unit.  The British planes are closing on the balloons.",
    ],
    objective: { kind: "guardBalloons", label: "Drive the S.E.5s off the balloon unit", count: 3 },
  },
  allied: {
    aceIndex: undefined,
    unnamedPilot: 101,
    aircraftCode: 6,
    playable: true,
    lines: [
      "You are the commander of the Allied squadron.  You start as you",
      "arrive near the German balloon unit.",
    ],
    objective: { kind: "burnBalloons", label: "Burn the German balloons" },
  },
  portNotes: [
    "*\"Several Allied planes\"* and *\"Udet and his men\"* are three each — the smallest count the plural supports, and the one this port's AI tuning was measured on.",
    "The S.E.5s carry incendiary, because they came for the gasbags; Udet's flight carries ball.",
  ],
};

/**
 * **6. Immelmann's First Victory** — the file's only one-sided encounter. The
 * British seat is `FF FF FF`, and `PS.EXE` refuses it with *"You cannot play
 * the %b side of this mission."*
 */
const IMMELMANN: HistoricEncounterData = {
  id: "historic-6",
  title: "Immelmann's First Victory",
  when: "1 August 1915",
  era: "early",
  region: "dunkirk",
  weather: { sky: "clear", wind: "still", timeOfDay: "dawn" },
  briefing: [
    "A British bombing raid surprised the Germans at the Douai aerodrome.",
    "Oswald Boelcke jumped into his Eindecker and took off in pursuit of the",
    "British planes.  Max Immelmann followed a few minutes later in a second",
    "Fokker.  At 10,000 feet Immelmann discovered two of the British planes.",
  ],
  flights: [
    {
      id: "immelmann",
      nation: "german",
      aircraft: "FOKE3",
      callsign: "Max Immelmann",
      skill: "ace",
      aceId: "immelmann",
      seat: "german",
      x: 0,
      altitudeM: 3050,
      z: 5300,
      headingRad: 0,
    },
    BE2_LEADER,
    wingman(BE2_LEADER, { id: "be2-2", callsign: "B.E.2c (2)", skill: "novice", dx: 400, dy: -20, dz: -100 }),
  ],
  german: {
    aceIndex: 6,
    aircraftCode: 18,
    playable: true,
    lines: [
      "You are Immelmann.  You begin above the British planes.  Boelcke",
      "has returned to the aerodrome.",
    ],
    objective: { kind: "destroyAircraft", label: "Bring down the two British machines", count: 2 },
  },
  allied: {
    aceIndex: undefined,
    aircraftCode: 0,
    playable: false,
    lines: [],
    objective: { kind: "destroyAircraft", label: "", count: 0 },
  },
  portNotes: [
    "10,000 feet is the briefing's own number, and this is the only encounter flown above 3 000 m — where the engine lapse bites and none of the AI tuning was measured. Expect it to fly differently from the other seven, because it does.",
    "The file gives the British machines no aircraft code — their seat is `FF FF FF` — so this port flies them as B.E.2cs, the raider the RFC had over Douai in August 1915. That choice is port-side.",
    "Boelcke is in the briefing and not in the air, because the German seat's own line says he *\"has returned to the aerodrome.\"*",
  ],
};

/**
 * **7. Frank Luke, Balloon-Buster** — one stolen SPAD against three Drachen and
 * their guard. The file's only encounter whose *Allied* seat is a named ace and
 * whose German seat is not.
 */
const LUKE: HistoricEncounterData = {
  id: "historic-7",
  title: "Frank Luke, Balloon-Buster",
  when: "September 1918",
  era: "late",
  region: "verdun",
  weather: { sky: "partly cloudy", wind: "light", timeOfDay: "dusk" },
  briefing: [
    "After his commanding officer grounded him for going AWOL twice,",
    "Frank Luke decided to steal a Spad and go balloon hunting.  He",
    "discovered three German balloons near the Meuse River.  Alone,",
    "he headed toward the German balloons.",
  ],
  balloonNation: "german",
  balloons: [
    { id: "balloon-1", nation: "german", label: "Drachen 1", x: -560, altitudeM: BALLOON_ALTITUDE_M, z: 2850 },
    { id: "balloon-2", nation: "german", label: "Drachen 2", x: 60, altitudeM: BALLOON_ALTITUDE_M, z: 3020 },
    { id: "balloon-3", nation: "german", label: "Drachen 3", x: 640, altitudeM: BALLOON_ALTITUDE_M, z: 2840 },
  ],
  flights: [
    {
      id: "luke",
      nation: "allied",
      aircraft: "SPAD_7",
      callsign: "Frank Luke",
      skill: "ace",
      aceId: "luke",
      seat: "allied",
      x: 0,
      altitudeM: 1900,
      z: 2900,
      headingRad: Math.PI,
    },
    GUARD_LEADER,
    wingman(GUARD_LEADER, { id: "guard-2", callsign: "Balloon guard (2)", skill: "regular", dx: -700, dy: -30, dz: 40 }),
    wingman(GUARD_LEADER, { id: "guard-3", callsign: "Balloon guard (3)", skill: "regular", dx: -340, dy: 50, dz: 120 }),
  ],
  german: {
    aceIndex: undefined,
    unnamedPilot: 100,
    aircraftCode: 24,
    playable: true,
    lines: [
      "You are the commander of a German squadron assigned to defend",
      "the balloon unit.  You will start above the balloons.",
    ],
    objective: { kind: "guardBalloons", label: "Stop Luke before he burns the balloons", count: 1 },
  },
  allied: {
    aceIndex: 19,
    aircraftCode: 5,
    playable: true,
    lines: ["You are Luke.  You will start above the German balloons."],
    objective: { kind: "burnBalloons", label: "Burn all three German balloons" },
  },
  portNotes: [
    "Both seats' own lines put them *above the balloons*, which is what this layout does — Luke lower and already committed, the guard higher and behind him.",
    "Luke's SPAD carries incendiary. Alone means alone: he has no wingman on either seat's telling.",
  ],
};

/**
 * **8. Richthofen Cheats Fate** — June 1917, Jasta 11 caught over its own side
 * of the wire by Naval 10's Triplanes. The two seats describe two different
 * moments of the same fight; this port flies the later one, with everybody
 * present.
 */
const CHEATS_FATE: HistoricEncounterData = {
  id: "historic-8",
  title: "Richthofen Cheats Fate",
  when: "June 1917",
  era: "mid",
  region: "dunkirk",
  weather: { sky: "cloudy", wind: "light", timeOfDay: "daytime" },
  briefing: [
    "On a routine sweep behind German lines, 20 Squadron found itself",
    "cornered by Manfred von Richthofen's Jasta 11.  The Germans dove",
    "and engaged the British F.E.2s, shooting down two of them.",
    "At this point several Sopwith Triplanes of Naval 10 Squadron",
    "arrived to rescue the hard-pressed F.E.2s.",
  ],
  flights: [
    JASTA11_LEADER,
    wingman(JASTA11_LEADER, { id: "jasta11-2", callsign: "Jasta 11 (2)", skill: "veteran", dx: -280, dy: -40, dz: 130 }),
    FE2_LEADER,
    wingman(FE2_LEADER, { id: "fe2-2", callsign: "F.E.2b (2)", skill: "regular", dx: 420, dy: -20, dz: -100 }),
    TRIPLANE_LEADER,
    wingman(TRIPLANE_LEADER, { id: "triplane-2", callsign: "Naval 10 (2)", skill: "veteran", dx: -230, dy: -30, dz: -120 }),
  ],
  german: {
    aceIndex: 0,
    aircraftCode: 20,
    playable: true,
    lines: [
      "You are Richthofen.  You will start slightly above the F.E.2s before",
      "the Triplanes arrive.",
    ],
    objective: { kind: "destroyAircraft", label: "Finish the F.E.2s and beat off the Triplanes", count: 2 },
  },
  allied: {
    aceIndex: undefined,
    unnamedPilot: 101,
    aircraftCode: 4,
    playable: true,
    lines: [
      "You are the commander of the Triplanes.  You will start just after",
      "the Germans have engaged the F.E.2s.",
    ],
    objective: { kind: "destroyAircraft", label: "Break up Jasta 11 and get the F.E.2s out", count: 2 },
  },
  portNotes: [
    "The two seats' lines describe different instants — *\"before the Triplanes arrive\"* and *\"just after the Germans have engaged\"* — and a `MissionSpec` has one start time, so this port flies the later one from both seats. The German seat therefore begins with the Triplanes already inbound rather than a minute clear of them.",
    "*\"Shooting down two of them\"* has already happened when the encounter opens: two F.E.2s are on the map, not four.",
  ],
};

export const HISTORIC_SCENES_B: readonly HistoricEncounterData[] = [UDET, IMMELMANN, LUKE, CHEATS_FATE];
